"use strict";
/**
 * The EclipseGG admin panel's sign-in contract (platform spec §4.2, 2026-10-19).
 *
 * The seller's OWN accounts, never a venue's: a `PlatformAdmin` signs in with
 * an email, a password and a two-step code from an authenticator app, on the
 * admin host only. Nothing here is shared with staff sign-in — not the
 * secrets, not the cookie, not the throttle — so a venue's staff session can
 * never be turned into the seller's, and the reverse.
 *
 * Two steps, because the code is checked only after the password:
 *
 *  1. `POST /api/platform/auth/login` answers a short-lived challenge — to
 *     type a code (`step: 'totp'`), or, on the very first sign-in, to set the
 *     authenticator up (`step: 'enroll'`, with the secret as an `otpauth://`
 *     link the admin site draws as a QR code itself, never through an outside
 *     QR service).
 *  2. `…/verify` (or `…/enroll`) takes the challenge and the code and signs
 *     in. Enrolling answers ten recovery codes, ONCE: they are stored only as
 *     argon2 hashes and can never be shown again.
 *
 * English only: the panel is the seller's tool (spec §4.6).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.platformChangePasswordSchema = exports.platformFreshCodeSchema = exports.platformVerifySchema = exports.platformEnrollSchema = exports.platformLoginSchema = exports.PLATFORM_ADMIN_MIN_PASSWORD = exports.PLATFORM_FRESH_CODE_SECONDS = void 0;
const zod_1 = require("zod");
/**
 * How recently a two-step code must have been typed for the dangerous venue
 * actions — suspend, reactivate, owner-password reset, ending sittings (spec
 * §4.2). Five minutes: long enough to finish the dialog that asked for it.
 */
exports.PLATFORM_FRESH_CODE_SECONDS = 300;
/** The admin password floor — the first-admin script refuses the same. */
exports.PLATFORM_ADMIN_MIN_PASSWORD = 12;
/** A six-digit authenticator code, spaces trimmed. */
const totpCode = zod_1.z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.');
exports.platformLoginSchema = zod_1.z.object({
    /** Lowercased here: the column is unique on the lowercase address. */
    email: zod_1.z.string().trim().toLowerCase().email().max(254),
    password: zod_1.z.string().min(1).max(200),
});
exports.platformEnrollSchema = zod_1.z.object({
    challengeToken: zod_1.z.string().min(1).max(4096),
    code: totpCode,
});
/**
 * The code step: an authenticator code OR one recovery code, never both.
 * A recovery code signs in only — it never counts as a fresh code.
 */
exports.platformVerifySchema = zod_1.z
    .object({
    challengeToken: zod_1.z.string().min(1).max(4096),
    code: totpCode.optional(),
    recoveryCode: zod_1.z.string().trim().min(1).max(40).optional(),
})
    .refine((value) => Boolean(value.code) !== Boolean(value.recoveryCode), {
    message: 'Send a code or a recovery code.',
});
/** Re-proving a code before a dangerous action (`POST …/fresh-code`). */
exports.platformFreshCodeSchema = zod_1.z.object({ code: totpCode });
exports.platformChangePasswordSchema = zod_1.z
    .object({
    currentPassword: zod_1.z.string().min(1).max(200),
    newPassword: zod_1.z.string().min(exports.PLATFORM_ADMIN_MIN_PASSWORD).max(200),
})
    .refine((value) => value.currentPassword !== value.newPassword, {
    path: ['newPassword'],
    message: 'Choose a different password.',
});
//# sourceMappingURL=platform-auth.js.map